import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Film, ArrowRight } from 'lucide-react';
import { photoUtils } from '../lib/photoUtils';
import PhotoModal from './PhotoModal';

const FeaturedPhotos = () => {
    const [featuredMedia, setFeaturedMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const fetchFeaturedMedia = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch both photos and videos
                const [photos, videos] = await Promise.all([
                    photoUtils.getPhotos(),
                    photoUtils.getVideos(),
                ]);

                // Filter for items flagged as featured (title ends with "..."),
                // newest first, across both photos and videos.
                const featured = [...photos, ...videos]
                    .filter(item => item.title && item.title.trim().endsWith('...'))
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                // Limit to 6 items
                setFeaturedMedia(featured.slice(0, 6));
            } catch (error) {
                console.error('Error fetching featured media:', error);
                setError('Failed to load featured media');
            } finally {
                setLoading(false);
            }
        };

        fetchFeaturedMedia();
    }, []);

    if (loading) {
        return (
            <div className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || featuredMedia.length === 0) {
        return null; // Don't show the section if there's an error or no featured media
    }

    return (
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-purple-50/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between mb-10">
                    <div>
                        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-purple-600">
                            <Camera className="h-4 w-4" />
                            Gallery
                        </span>
                        <h2 className="mt-3 font-display text-4xl font-semibold text-gray-900">Featured Moments</h2>
                        <p className="mt-2 text-lg text-gray-600">Highlights from our community activities</p>
                    </div>
                    <Link
                        to="/photos"
                        className="group inline-flex items-center self-start rounded-full border border-purple-200 bg-white
                                 px-5 py-2.5 text-sm font-semibold text-purple-700 shadow-sm transition-all
                                 hover:-translate-y-0.5 hover:border-purple-300 hover:shadow-md md:self-auto"
                    >
                        View All Photos &amp; Videos
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>

                {/* Up to 6 tiles — smaller on larger screens so all fit in a single tidy row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                    {featuredMedia.map((item) => {
                        const TypeIcon = item.type === 'video' ? Film : Camera;
                        return (
                            <button
                                key={item.$id}
                                type="button"
                                onClick={() => setSelectedItem(item)}
                                className="group relative aspect-square w-full overflow-hidden rounded-2xl bg-gray-100
                                         ring-1 ring-black/5 shadow-sm transition-all duration-300
                                         hover:-translate-y-1 hover:shadow-xl hover:ring-purple-200
                                         focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
                            >
                                {item.type === 'video' ? (
                                    <video
                                        src={item.fileUrl}
                                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                                        muted
                                        preload="metadata"
                                    />
                                ) : (
                                    <img
                                        src={item.fileUrl}
                                        alt={item.title}
                                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                                    />
                                )}

                                {/* Media-type chip */}
                                <span className="absolute left-2.5 top-2.5 grid h-7 w-7 place-items-center rounded-full bg-black/45 backdrop-blur-sm">
                                    <TypeIcon className="h-3.5 w-3.5 text-white" />
                                </span>

                                {/* Persistent subtle base gradient + caption reveal on hover */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 translate-y-2 p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                                    <h3 className="truncate text-sm font-semibold text-white">
                                        {item.title?.replace(/\.\.\.$/, '')}
                                    </h3>
                                    {item.category && (
                                        <p className="truncate text-xs text-white/75">{item.category}</p>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Lightbox popup */}
            {selectedItem && (
                <PhotoModal
                    photo={{ ...selectedItem, title: selectedItem.title?.replace(/\.\.\.$/, '') }}
                    onClose={() => setSelectedItem(null)}
                    isAdmin={false}
                    onDelete={() => {}}
                />
            )}
        </section>
    );
};

export default FeaturedPhotos; 